import { component$, useContext, type JSX } from '@builder.io/qwik';
import type {
  SportradarFactoryProps,
  SportradarPropsType,
} from '~/types/SportradarTypes';
import {
  buildSportradarFromName,
  buildSportradarProps,
} from '~/utilities/elements/buildSportradar';
import Sportradar from '~/components/Shared/Sportradar/Sportradar';
import { Logger } from '~/utilities/logger';
import { cva } from 'class-variance-authority';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getCSSColorText, getCSSColorBg } from '~/utilities/getCSS';
import Tag from '~/components/Shared/Tags/Tag';

const cssContainer = cva('', {
  variants: {
    backgroundColor: getCSSColorBg(),
    position: {
      sidebar: 'mb-10',
      other: 'mb-5 p-5 shadow-widget relative z-10',
    },
  },
});

const cssTitle = cva('mb-2.5', {
  variants: {
    fontColor: getCSSColorText(),
  },
});

export default component$(
  ({
    sportradarList,
    name,
    sportradarProps,
    wrapperClass,
    widgetClass,
    buttonTitle,
    buttonUrl,
    position = 'other',
    html,
  }: SportradarFactoryProps): JSX.Element => {
    const {
      themeComponents: { sportradar },
    } = useContext(AppDataContext);

    const getWidgets = (): SportradarPropsType[] => {
      if (sportradarList?.length) {
        return sportradarList;
      }

      if (!name && !sportradarList?.length) {
        new Logger().error('Something went wrong with the sportradar widget');
        return [];
      }

      return buildSportradarFromName(name as string, sportradarProps || {});
    };

    const widgets = getWidgets();

    return widgets.length
      ? widgets.map((item: SportradarPropsType, index: number) => {
          return (
            <div
              key={`sr-wrapper-${index}`}
              class={cssContainer({
                backgroundColor: sportradar.backgroundColor.value,
                position: position,
                class: wrapperClass,
              })}
            >
              {item.title && (
                <h4
                  class={cssTitle({ fontColor: sportradar.titleColor.value })}
                >
                  {item.title}
                </h4>
              )}
              <Sportradar
                class={widgetClass}
                dataId={item.dataId as string}
                widgetId={item.widgetId as string}
                language={item.language || 'en_us'}
                sportradarProps={buildSportradarProps(widgets, item, index)}
                html={html}
              />
              {buttonTitle && buttonUrl ? (
                <div class='mt-3'>
                  <Tag link={buttonUrl} text={buttonTitle} />
                </div>
              ) : null}
            </div>
          );
        })
      : null;
  }
);
